import { timeFormat } from 'd3-time-format'
import { format as d3Format } from 'd3-format'
import { textPropsByEngine } from '@dfnivo/core'
import { ScaleValue, AnyScale, TicksSpec, getScaleTicks, centerScale } from '@dfnivo/scales'
import { Point, ValueFormatter, Line } from './types'
import { timeSecond, timeMinute, timeHour, timeMonth, timeDay, timeYear } from 'd3-time'
const isArray = <T>(value: unknown): value is T[] => Array.isArray(value)

export function computeDefaultTickAmounts({
    length,
    axis,
    scale,
}: {
    scale: AnyScale
    length: number
    axis: 'x' | 'y'
}) {
    let defaultValues: number
    if (scale.type === 'linear') {
        defaultValues = axis === 'x' ? length / 100 : length / 50
    } else {
        defaultValues = axis === 'x' ? length / 200 : length / 50
    }
    return defaultValues
}

export const computeCartesianTicks = <Value extends ScaleValue>({
    axis,
    scale,
    ticksPosition,
    tickValues,
    tickSize,
    tickPadding,
    tickRotation,
    engine = 'svg',
    length,
}: {
    axis: 'x' | 'y'
    scale: AnyScale
    ticksPosition?: 'after' | 'before'
    tickValues?: TicksSpec<Value>
    length: number
    tickSize: number
    tickPadding: number
    tickRotation: number
    engine?: 'svg' | 'canvas'
}) => {
    tickValues =
        tickValues ??
        computeDefaultTickAmounts({
            length,
            axis,
            scale,
        })
    const values = getScaleTicks<Value>(scale, tickValues)

    const textProps = textPropsByEngine[engine]

    const position = 'bandwidth' in scale ? centerScale(scale) : scale
    const line = { lineX: 0, lineY: 0 }
    const text = { textX: 0, textY: 0 }

    const isRTL = typeof document === 'object' ? document.dir === 'rtl' : false
    let translate: (value: Value) => Point
    let textAlign = (textProps.align.center || 'center') as CanvasTextAlign
    let textBaseline = (textProps.baseline.center || 'center') as CanvasTextBaseline

    if (axis === 'x') {
        translate = d => ({ x: position(d) ?? 0, y: 0 })

        line.lineY = tickSize * (ticksPosition === 'after' ? 1 : -1)
        text.textY = (tickSize + tickPadding) * (ticksPosition === 'after' ? 1 : -1)

        if (ticksPosition === 'after') {
            textBaseline = textProps.baseline.top as 'top'
        } else {
            textBaseline = textProps.baseline.bottom as 'bottom'
        }

        if (tickRotation === 0) {
            textAlign = textProps.align.center as 'center'
        } else if (
            (ticksPosition === 'after' && tickRotation < 0) ||
            (ticksPosition === 'before' && tickRotation > 0)
        ) {
            textAlign = textProps.align[isRTL ? 'left' : 'right']
            textBaseline = textProps.baseline.center as CanvasTextBaseline
        } else if (
            (ticksPosition === 'after' && tickRotation > 0) ||
            (ticksPosition === 'before' && tickRotation < 0)
        ) {
            textAlign = textProps.align[isRTL ? 'right' : 'left']
            textBaseline = textProps.baseline.center as CanvasTextBaseline
        }
    } else {
        translate = d => ({ x: 0, y: position(d) ?? 0 })

        line.lineX = tickSize * (ticksPosition === 'after' ? 1 : -1)
        text.textX = (tickSize + tickPadding) * (ticksPosition === 'after' ? 1 : -1)

        if (ticksPosition === 'after') {
            textAlign = textProps.align.left
        } else {
            textAlign = textProps.align.right
        }
    }

    const ticks = values.map(value => ({
        key: typeof value === 'number' || typeof value === 'string' ? value : `${value}`,
        value,
        ...translate(value),
        ...line,
        ...text,
    }))

    return {
        ticks,
        textAlign,
        textBaseline,
    }
}

export const getFormatter = <Value extends ScaleValue>(
    format: string | ValueFormatter<Value> | undefined,
    scale: AnyScale
): ValueFormatter<Value> | undefined => {
    if (typeof format === 'function') {
        return format
    }
    switch (scale.type) {
        case 'linear': {
            const [min, max] = scale.domain()
            const domainRange = Math.abs(max - min)
            let numberFormat: Intl.NumberFormat
            if (domainRange >= 10) {
                numberFormat = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 1 })
            } else if (domainRange >= 0.1) {
                numberFormat = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 2 })
            } else {
                numberFormat = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 })
            }
            return (value: unknown) => numberFormat.format(Number(value))
        }
        case 'time': {
            const [start, end] = scale.domain()
            let dateFormat: string
            if (Math.abs(timeSecond.count(start, end)) < 1) {
                dateFormat = '%S,%L'
            } else if (Math.abs(timeSecond.count(start, end)) < 30) {
                dateFormat = '%S'
            } else if (Math.abs(timeMinute.count(start, end)) < 30) {
                dateFormat = '%M:%S'
            } else if (Math.abs(timeHour.count(start, end)) < 12) {
                dateFormat = '%H:%M:%S'
            } else if (Math.abs(timeDay.count(start, end)) < 4) {
                dateFormat = '%e %H:%M'
            } else if (Math.abs(timeMonth.count(start, end)) < 4) {
                dateFormat = '%b %e'
            } else if (Math.abs(timeYear.count(start, end)) < 1) {
                dateFormat = '%B'
            } else if (Math.abs(timeYear.count(start, end)) < 4) {
                dateFormat = '%Y %b'
            } else {
                dateFormat = '%Y'
            }
            return timeFormat(dateFormat) as ValueFormatter<Value>
        }
    }
    if (typeof format === 'undefined') {
        return format as undefined
    }

    return d3Format(format as string) as unknown as ValueFormatter<Value>
}

export const computeGridLines = <Value extends ScaleValue>({
    width,
    height,
    scale,
    axis,
    values: _values,
}: {
    width: number
    height: number
    scale: AnyScale
    axis: 'x' | 'y'
    values?: TicksSpec<Value>
}) => {
    _values =
        _values ??
        computeDefaultTickAmounts({
            length: axis === 'x' ? width : height,
            axis,
            scale,
        })
    const lineValues = isArray<number>(_values) ? _values : undefined
    const values = lineValues || getScaleTicks<Value>(scale, _values)
    const position = 'bandwidth' in scale ? centerScale(scale) : scale

    const lines: Line[] =
        axis === 'x'
            ? values.map(value => ({
                  key: `${value}`,
                  x1: position(value) ?? 0,
                  x2: position(value) ?? 0,
                  y1: 0,
                  y2: height,
              }))
            : values.map(value => ({
                  key: `${value}`,
                  x1: 0,
                  x2: width,
                  y1: position(value) ?? 0,
                  y2: position(value) ?? 0,
              }))

    return lines
}
